import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import Keycloak from "keycloak-js";
import keycloak from "./keycloak";
import {
  setTokens,
  loginSuccessful,
  loginFailed,
} from "../../../store/authentication/actions";
import { useDispatch } from "react-redux";
import { User } from "../../../types";

export const getCookie = (name: string): string | null => {
  const nameEQ = encodeURIComponent(name) + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0)
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
  }
  return null;
};

export const getUserFromCookie = (): User | null => {
  const userCookie = getCookie("user");
  return userCookie ? JSON.parse(userCookie) : null;
};

const setCookie = (name: string, value: string) => {
  const cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(
    value
  )}; path=/; secure; samesite=strict;`;
  document.cookie = cookieString;
};

const removeCookie = (name: string) => {
  document.cookie = `${encodeURIComponent(
    name
  )}=; max-age=-1; path=/; secure; samesite=strict;`;
};

export const setSessionCookies = (params: {
  accessToken?: string;
  refreshToken?: string;
  user?: User;
}) => {
  const { accessToken, refreshToken, user } = params;

  if (accessToken) {
    setCookie("access_token", accessToken);
  }

  if (refreshToken) {
    setCookie("refresh_token", refreshToken);
  }

  if (user) {
    setCookie("user", JSON.stringify(user));
  }
};

export const removeSessionCookies = (all: boolean = false) => {
  removeCookie("access_token");
  removeCookie("refresh_token");

  if (all) {
    removeCookie("user");
  }
};

export interface KeycloakContextProps {
  keycloak: Keycloak;
  keycloakInitialized: boolean;
}

const KeycloakContext = createContext<KeycloakContextProps | undefined>(
  undefined
);

export const KeycloakProvider = ({ children }: { children: ReactNode }) => {
  const [keycloakInitialized, setKeycloakInitialized] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    // check existing cookies
    const accessToken = getCookie("access_token");
    const refreshToken = getCookie("refresh_token");

    keycloak
      .init({
        onLoad: "check-sso",
        token: accessToken || undefined,
        refreshToken: refreshToken || undefined,
      })
      .then((authenticated) => {
        setKeycloakInitialized(true);

        console.log("authenticated: ", authenticated);

        if (authenticated) {
          console.log(keycloak.tokenParsed);
          console.log(keycloak.idTokenParsed);
          console.log(keycloak.refreshTokenParsed);

          const tokenData = {
            accessToken: keycloak.token,
            refreshToken: keycloak.refreshToken,
          };

          const { email, name, role_attributes } = keycloak.tokenParsed || {};
          const roleAttributes = role_attributes || {};
          const roles = Object.keys(roleAttributes);

          if (roles.length === 0) {
            dispatch(loginFailed("No roles found."));
          }

          const hasInternalRole = roles.some(
            (role) => role === "MPS_NZ" || role === "MPS_AUS"
          );

          const user = {
            username: email,
            customerId: 0, // customer Id is now handled by the backend, so doesn't really matter anymore
            role: hasInternalRole ? "INTERNAL" : "CLAIMHANDLER",
            fullname: name,
            password: "",
          };

          console.log(user);

          setSessionCookies({ ...tokenData, user });

          dispatch(setTokens(tokenData));
          dispatch(loginSuccessful(user, true));
        } else {
          if (keycloakInitialized) {
            keycloak.clearToken();
            removeSessionCookies(true);
            dispatch(loginFailed("User is not authenticated"));
          }
        }
      })
      .catch((err) => {
        console.error("Keycloak initialization failed", err);
        keycloak.clearToken();
        removeSessionCookies(true);
      });

    setupTokenRefresh();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupTokenRefresh = () => {
    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(30)
        .then((refreshed) => {
          if (refreshed) {
            console.log("auto refreshed token");
            const tokenData = {
              accessToken: keycloak.token,
              refreshToken: keycloak.refreshToken,
            };
            setSessionCookies({ ...tokenData });
            dispatch(setTokens(tokenData));
          } else {
            console.warn("auto refresh: token is still valid");
          }
        })
        .catch((err) => {
          console.error("Failed to auto refresh token", err);
          keycloak.clearToken();
          removeSessionCookies(true);
          dispatch(loginFailed("Failed to refresh token"));
        });
    };
  };

  return (
    <KeycloakContext.Provider value={{ keycloak, keycloakInitialized }}>
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloak = (): KeycloakContextProps => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error("useKeycloak must be used within a KeycloakProvider");
  }
  return context;
};

export const withKeycloak =
  <P extends object>(
    Component: React.ComponentType<
      P & { keycloakContext?: KeycloakContextProps }
    >
  ): React.FC<P> =>
  (props) =>
    (
      <KeycloakContext.Consumer>
        {(context) => {
          if (!context) {
            throw new Error(
              "withKeycloak must be used within a KeycloakProvider"
            );
          }
          return <Component {...props} keycloakContext={context} />;
        }}
      </KeycloakContext.Consumer>
    );
